/**
 * "New from document" onboarding wizard (Documents Phase 2).
 *
 * Tier 1: trade license upload → STAGING (never the vault) → AI extraction
 * prefills the client create form → atomic complete (client + vault doc
 * CONFIRMED in one transaction). Duplicate guard on TRN / trade license no.
 * offers "open existing" or "create anyway".
 *
 * Tiers 2/3: VAT and corporate tax certificates (both skippable) stage then
 * attach as CONFIRMED with mismatch radios — the trade-license (client) value
 * wins by default; picking the document value writes it via applyConflicts.
 * Tier 3 additionally asks for the financial year end (clients.fyEnd).
 *
 * Tier 4: the existing ApplyTemplateModal, now evidence-driven off the
 * documents just attached.
 *
 * The whole wizard runs on the clients.create capability.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  COUNTRIES,
  JURISDICTIONS,
  CLIENT_DOCUMENT_TYPE_LABELS,
  clientStatusSchema,
  type ClientDocumentType,
  type ClientOutput,
  type ConfirmClientDocumentOutput,
  type ConfirmableField,
  type CreateClientInput,
  type DuplicateClientMatch,
  type OnboardingDocumentOutput,
  type OnboardingUploadOutput,
} from '@1financial/shared';
import { getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { useEscape } from '../hooks/useEscape.js';
import { SearchableSelect } from './SearchableSelect.js';
import { ApplyTemplateModal } from './ApplyTemplateModal.js';
import { TaxCertificateDates, taxReviewValues, taxReviewPayload } from './TaxCertificateDates.js';
import { DateInput, validateDateInputs } from './DateInput.js';
import { fmtDate } from '../lib/date.js';

const MAX_FILE_BYTES = 20 * 1024 * 1024;

function authHeaders(): HeadersInit {
  return { Authorization: `Bearer ${getAccessToken() ?? ''}` };
}

const STEP_LABELS = ['Trade license', 'Client details', 'VAT certificate', 'Corporate tax', 'Template'] as const;
type Step = 'tl-upload' | 'client-form' | 'vat' | 'ct' | 'template';

function stepIndex(step: Step): number {
  return { 'tl-upload': 0, 'client-form': 1, vat: 2, ct: 3, template: 4 }[step];
}

const ATTACH_FIELD_LABELS: Record<'legalName' | 'trn' | 'fyEnd', string> = {
  legalName: 'Legal name',
  trn: 'TRN',
  fyEnd: 'Financial year end',
};

/**
 * License "activities" arrives newline/semicolon-separated — joining raw would
 * concatenate entries inside the single-line Industry input. Join with ", "
 * and keep only whole entries that fit the 100-char column limit.
 */
function activitiesToIndustry(activities: string): string {
  const parts = activities.split(/[\r\n;•]+/).map((s) => s.trim()).filter(Boolean);
  let out = '';
  for (const part of parts) {
    const next = out ? `${out}, ${part}` : part;
    if (next.length > 100) break;
    out = next;
  }
  return out || activities.slice(0, 100);
}

export function OnboardingWizard({ onClose, onSuccess, resumeId }: { onClose: () => void; onSuccess: (clientId: string) => void; resumeId?: string | null }) {
  useEscape(onClose);
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(resumeId ? 'client-form' : 'tl-upload');
  const [tlStaged, setTlStaged] = useState<{ doc: OnboardingDocumentOutput; duplicates: DuplicateClientMatch[] } | null>(null);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [client, setClient] = useState<ClientOutput | null>(null);
  const [showTemplate, setShowTemplate] = useState(false);

  // Chat-launcher deep link (/clients?onboard=<stagingId>): load the staged
  // document and drop straight into the review step.
  useEffect(() => {
    if (!resumeId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/v1/onboarding/documents/${resumeId}`, { headers: authHeaders() });
        const body = await res.json() as { ok?: boolean; data?: OnboardingUploadOutput; error?: { message?: string } };
        if (cancelled) return;
        if (!res.ok || !body.ok || !body.data) {
          setResumeError(body.error?.message ?? 'Staged document not found — stages expire after 24h. Upload it again below.');
          setStep('tl-upload');
          return;
        }
        if (body.data.document.status !== 'PENDING') {
          setResumeError('That staged document was already used. Upload a fresh one below.');
          setStep('tl-upload');
          return;
        }
        setTlStaged({ doc: body.data.document, duplicates: body.data.duplicates });
      } catch (err) {
        if (!cancelled) {
          setResumeError((err as Error).message);
          setStep('tl-upload');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [resumeId]);

  function openExisting(id: string) {
    onClose();
    navigate(`/clients/${id}`);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-5xl max-h-[92vh] flex flex-col mx-4 shadow-2xl animate-pop">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="text-base font-semibold text-white">New client from document</h2>
            <p className="text-xs text-muted mt-0.5">
              Step {stepIndex(step) + 1} of {STEP_LABELS.length} · {STEP_LABELS[stepIndex(step)]}
            </p>
          </div>
          <button onClick={onClose} className="text-muted hover:text-white text-sm">✕</button>
        </div>

        <div className="px-5 py-4 overflow-y-auto">
          {resumeError && (
            <p className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 mb-3">{resumeError}</p>
          )}
          {step === 'client-form' && resumeId && !tlStaged && !resumeError && (
            <div className="text-muted p-8 text-center text-sm">Loading staged document…</div>
          )}
          {step === 'tl-upload' && (
            <TradeLicenseUpload
              onUploaded={(doc, duplicates) => { setTlStaged({ doc, duplicates }); setStep('client-form'); }}
            />
          )}
          {step === 'client-form' && tlStaged && (
            <ClientFormStep
              doc={tlStaged.doc}
              initialDuplicates={tlStaged.duplicates}
              onCreated={(c) => { setClient(c); setStep('vat'); }}
              openExisting={openExisting}
            />
          )}
          {step === 'vat' && client && (
            <CertificateStep
              key="vat"
              client={client}
              type="VAT_CERTIFICATE"
              askFyEnd={false}
              onSkip={() => setStep('ct')}
              onAttached={(c) => { setClient(c); setStep('ct'); }}
            />
          )}
          {step === 'ct' && client && (
            <CertificateStep
              key="ct"
              client={client}
              type="CORPORATE_TAX_CERTIFICATE"
              askFyEnd={true}
              onSkip={() => setStep('template')}
              onAttached={(c) => { setClient(c); setStep('template'); }}
            />
          )}
          {step === 'template' && client && !showTemplate && (
            <div className="max-w-lg mx-auto text-center py-8 space-y-4">
              <p className="text-emerald-300 text-sm bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
                Client “{client.name}” is ready.
              </p>
              <p className="text-sm text-white">Apply a compliance template now?</p>
              <p className="text-xs text-muted">
                Items backed by the documents you just attached are pre-ticked with deadlines derived from them.
              </p>
              <div className="flex justify-center gap-2 pt-2">
                <button
                  onClick={() => onSuccess(client.id)}
                  className="text-sm text-muted hover:text-white px-3 py-1.5"
                >
                  Finish without template
                </button>
                <button
                  onClick={() => setShowTemplate(true)}
                  className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
                >
                  Apply template
                </button>
              </div>
            </div>
          )}
          {step === 'template' && client && showTemplate && (
            <ApplyTemplateModal
              clientId={client.id}
              onClose={() => onSuccess(client.id)}
              onDone={() => onSuccess(client.id)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function TradeLicenseUpload({ onUploaded }: {
  onUploaded: (doc: OnboardingDocumentOutput, duplicates: DuplicateClientMatch[]) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    if (!file) { setError('Choose a trade license file first.'); return; }
    if (file.size > MAX_FILE_BYTES) { setError('File too large — max 20 MB.'); return; }
    setError(null);
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('type', 'TRADE_LICENSE');
      fd.append('file', file);
      const res = await fetch('/v1/onboarding/documents', { method: 'POST', headers: authHeaders(), body: fd });
      const body = await res.json() as { ok?: boolean; data?: OnboardingUploadOutput; error?: { message?: string } };
      if (!res.ok || !body.ok || !body.data) {
        setError(body.error?.message ?? `Error ${res.status}: ${res.statusText}`);
        return;
      }
      onUploaded(body.data.document, body.data.duplicates);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleUpload} className="max-w-lg mx-auto space-y-3 py-4">
      <p className="text-sm text-white">
        Upload the client's <span className="font-medium">trade license</span>. AI extraction prefills the
        client record — you review everything before anything is created.
      </p>
      <input
        type="file"
        accept="application/pdf,image/jpeg,image/jpg,image/png,image/webp"
        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        className="w-full text-sm text-muted file:mr-3 file:bg-bg-base file:border file:border-border file:rounded-lg file:px-3 file:py-1.5 file:text-sm file:text-white hover:file:border-accent"
      />
      <p className="text-xs text-muted">PDF, JPG, PNG or WebP — max 20 MB.</p>
      {error && <p className="text-xs text-rose-400 bg-rose-950/30 border border-rose-800 rounded-lg px-3 py-2">{error}</p>}
      <div className="flex justify-end pt-1">
        <button
          type="submit"
          disabled={submitting || !file}
          className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
        >
          {submitting ? 'Uploading & extracting…' : 'Upload & extract'}
        </button>
      </div>
    </form>
  );
}

function ClientFormStep({ doc, initialDuplicates, onCreated, openExisting }: {
  doc: OnboardingDocumentOutput;
  initialDuplicates: DuplicateClientMatch[];
  onCreated: (client: ClientOutput) => void;
  openExisting: (id: string) => void;
}) {
  const [duplicates, setDuplicates] = useState<DuplicateClientMatch[]>(initialDuplicates);
  const [allowDuplicate, setAllowDuplicate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Auth-gated preview — fetch as blob (same pattern as the vault review modal).
  const preview = useQuery({
    queryKey: ['onboarding-document-file', doc.id],
    queryFn: async () => {
      const res = await fetch(`/v1/onboarding/documents/${doc.id}/file`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      return URL.createObjectURL(await res.blob());
    },
    staleTime: Infinity,
  });
  useEffect(() => () => { if (preview.data) URL.revokeObjectURL(preview.data); }, [preview.data]);

  const extracted = doc.extracted ?? {};
  // Business rule (UAE trade licenses): the license ISSUE date doubles as the
  // incorporation date when no formation date is printed. The model stays
  // honest (establishmentDate null when none is printed) — the fallback is
  // applied here in the mapping layer, for TRADE_LICENSE only.
  const extractedIncorporation = doc.type === 'TRADE_LICENSE'
    ? (extracted.establishmentDate ?? extracted.issueDate ?? null)
    : (extracted.establishmentDate ?? null);
  const incorporationFromIssue = doc.type === 'TRADE_LICENSE' && !extracted.establishmentDate && !!extracted.issueDate;
  const [form, setForm] = useState<CreateClientInput>(() => ({
    // The manager/director person is the contact; the license's legalName is the company.
    name: extracted.managerName ?? extracted.legalName ?? '',
    company: extracted.legalName ?? null,
    email: null,
    phone: null,
    // UAE trade licenses are the wizard's staged type — prefill AE (country is optional).
    country: doc.type === 'TRADE_LICENSE' ? 'AE' : null,
    // License "activities" is a decent first guess for industry (capped to the column limit).
    industry: extracted.activities ? activitiesToIndustry(extracted.activities) : null,
    website: null,
    address: null,
    source: null,
    status: 'LEAD',
    ownerId: null,
    notesMarkdown: null,
    legalName: extracted.legalName ?? null,
    tradeLicenseNo: extracted.licenseNumber ?? null,
    trn: null,
    incorporationDate: extractedIncorporation ? `${extractedIncorporation}T00:00:00.000Z` : null,
    jurisdiction: 'UAE',
    fyEnd: null,
  }));

  function update<K extends keyof CreateClientInput>(field: K, value: CreateClientInput[K]) {
    setForm((f) => ({ ...f, [field]: value || null }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const missing: string[] = [];
    if (!form.name.trim()) missing.push('Name');
    // Company is optional (OPS 3.5 #2) — fresh registrations have no company
    // name yet. Country and industry are suggested (from the document), not blocking.
    if (missing.length > 0) {
      setError(`Required fields missing: ${missing.join(', ')}.`);
      return;
    }
    if (duplicates.length > 0 && !allowDuplicate) {
      setError('Possible duplicate below — open the existing client or choose “Create anyway”.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch('/v1/onboarding/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ stagingDocumentId: doc.id, allowDuplicate, client: form }),
      });
      const body = await res.json() as {
        ok?: boolean;
        data?: { client: ClientOutput };
        error?: { code?: string; message?: string };
      } & { data?: { client: ClientOutput; matches?: DuplicateClientMatch[] } };
      if (res.status === 409 && body.error?.code === 'DUPLICATE_CLIENT') {
        const matches = (body.data as { matches?: DuplicateClientMatch[] } | undefined)?.matches ?? [];
        setDuplicates(matches);
        setAllowDuplicate(false);
        setError('An existing client matches this TRN / trade license number — see below.');
        return;
      }
      if (!res.ok || !body.ok || !body.data?.client) {
        setError(body.error?.message ?? `Error ${res.status}: ${res.statusText}`);
        return;
      }
      onCreated(body.data.client);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      <div className="lg:w-1/2 min-h-[300px] bg-bg-base border border-border rounded-lg overflow-hidden flex items-center justify-center self-start">
        {preview.isLoading && <span className="text-muted text-sm p-6">Loading preview…</span>}
        {preview.error && <span className="text-rose-400 text-sm p-6">{(preview.error as Error).message}</span>}
        {preview.data && doc.mime.startsWith('image/') && (
          <img src={preview.data} alt={doc.originalName} className="max-h-[60vh] w-full object-contain" />
        )}
        {preview.data && !doc.mime.startsWith('image/') && (
          <iframe src={preview.data} title={doc.originalName} className="w-full h-[60vh]" />
        )}
      </div>

      <form onSubmit={handleSubmit} className="lg:w-1/2 space-y-3">
      {doc.extractionError && (
        <p className="text-xs text-rose-300 bg-rose-950/30 border border-rose-800 rounded-lg px-3 py-2">
          AI extraction failed{doc.extractionError ? `: ${doc.extractionError}` : ''}. Fill the legal fields manually against the document.
        </p>
      )}
      {!doc.extractionError && (
        <p className="text-xs text-muted">
          Prefilled from “{doc.originalName}” — review and complete the required fields.
        </p>
      )}

      {duplicates.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 space-y-2">
          <p className="text-xs font-medium text-amber-300">Possible duplicate — an existing client matches this document:</p>
          {duplicates.map((m) => (
            <div key={m.id} className="flex flex-wrap items-center justify-between gap-2 text-xs">
              <span className="text-white">
                {m.name}
                {m.company && <span className="text-muted"> · {m.company}</span>}
                <span className="text-amber-300/80"> (matches: {m.matchedOn.map((k) => (k === 'trn' ? 'TRN' : 'trade license no.')).join(', ')})</span>
              </span>
              {!m.hidden && <button type="button" onClick={() => openExisting(m.id)} className="text-accent hover:text-white border border-border hover:border-accent px-2 py-1 rounded">
                Open existing client
              </button>}
            </div>
          ))}
          {allowDuplicate ? (
            <p className="text-xs text-amber-300">
              Will create a new client despite the match.{' '}
              <button type="button" onClick={() => setAllowDuplicate(false)} className="underline">Undo</button>
            </p>
          ) : (
            <button type="button" onClick={() => { setAllowDuplicate(true); setError(null); }} className="text-xs text-rose-300 hover:text-white border border-rose-800 px-2 py-1 rounded">
              Create anyway
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <WizardField label="Name" required>
          <input
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            placeholder="Full name or company"
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted focus:outline-none focus:border-accent"
            required
          />
        </WizardField>
        <WizardField label="Company" hint="Optional — fresh registrations have no company name yet.">
          <input
            value={form.company ?? ''}
            onChange={(e) => update('company', e.target.value)}
            placeholder="Legal entity name"
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted focus:outline-none focus:border-accent"
          />
        </WizardField>
        <WizardField label="Country" hint="Suggested from the document — optional.">
          <SearchableSelect
            value={form.country ?? ''}
            onChange={(v) => update('country', v)}
            placeholder="— select —"
            options={COUNTRIES.map((c) => ({ value: c.code, label: c.label }))}
          />
        </WizardField>
        <WizardField label="Industry" hint="Suggested from the license activities — optional.">
          <input
            value={form.industry ?? ''}
            onChange={(e) => update('industry', e.target.value)}
            placeholder="e.g. Financial Services"
            maxLength={100}
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted focus:outline-none focus:border-accent"
          />
        </WizardField>
        <WizardField label="Email">
          <input
            type="email"
            value={form.email ?? ''}
            onChange={(e) => update('email', e.target.value)}
            placeholder="contact@company.com"
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted focus:outline-none focus:border-accent"
          />
        </WizardField>
        <WizardField label="Phone">
          <input
            value={form.phone ?? ''}
            onChange={(e) => update('phone', e.target.value)}
            placeholder="+971 50 123 4567"
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted focus:outline-none focus:border-accent"
          />
        </WizardField>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-border/50 pt-3">
        <WizardField label="Legal name">
          <input
            value={form.legalName ?? ''}
            onChange={(e) => update('legalName', e.target.value)}
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted focus:outline-none focus:border-accent"
          />
        </WizardField>
        <WizardField label="Jurisdiction">
          <select
            value={form.jurisdiction ?? ''}
            onChange={(e) => update('jurisdiction', (e.target.value || null) as CreateClientInput['jurisdiction'])}
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
          >
            <option value="">—</option>
            {JURISDICTIONS.map((j) => (
              <option key={j.code} value={j.code}>{j.label}</option>
            ))}
          </select>
        </WizardField>
        <WizardField label="Trade license no.">
          <input
            value={form.tradeLicenseNo ?? ''}
            onChange={(e) => update('tradeLicenseNo', e.target.value)}
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted focus:outline-none focus:border-accent"
          />
        </WizardField>
        <WizardField
          label="Incorporation date"
          hint={incorporationFromIssue ? 'Taken from the license issue date — the license prints no separate formation date. Override if different.' : undefined}
        >
          <DateInput
            value={form.incorporationDate ? form.incorporationDate.slice(0, 10) : ''}
            onChange={(e) => update('incorporationDate', e.target.value ? `${e.target.value}T00:00:00.000Z` : null)}
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
          />
        </WizardField>
        <WizardField label="Status" required>
          <select
            value={form.status ?? 'LEAD'}
            onChange={(e) => update('status', e.target.value as ClientOutput['status'])}
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
            required
          >
            {clientStatusSchema.options.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </WizardField>
      </div>

      {error && <p className="text-xs text-rose-400 bg-rose-950/30 border border-rose-800 rounded-lg px-3 py-2">{error}</p>}

      <div className="flex justify-end gap-2 pt-1">
        <button
          type="submit"
          disabled={submitting || (duplicates.length > 0 && !allowDuplicate)}
          className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
        >
          {submitting ? 'Creating…' : 'Create client'}
        </button>
      </div>
      </form>
    </div>
  );
}

/** One mismatchable attach field: editable document value + keep/use radios on conflict. */
function AttachFieldRow({ label, current, value, onChange, choice, onChoice, date = false }: {
  date?: boolean;
  label: string;
  current: string;
  value: string;
  onChange: (v: string) => void;
  choice: 'keep' | 'use';
  onChoice: (c: 'keep' | 'use') => void;
}) {
  const trimmed = value.trim();
  const conflict = trimmed !== '' && current !== '' && current !== trimmed;
  return (
    <div>
      <label className="block text-xs text-muted mb-1">{label}</label>
      {date ? <DateInput value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent" /> : <input value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent" />}
      {conflict && (
        <div className="mt-1.5 text-xs bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 space-y-1.5">
          <p className="text-amber-300">Does not match the client record — pick which value is correct:</p>
          <label className="flex items-center gap-2 text-white cursor-pointer">
            <input type="radio" checked={choice === 'keep'} onChange={() => onChoice('keep')} className="accent-emerald-400" />
            Keep client value “{date ? fmtDate(current) : current}”
          </label>
          <label className="flex items-center gap-2 text-white cursor-pointer">
            <input type="radio" checked={choice === 'use'} onChange={() => onChoice('use')} className="accent-emerald-400" />
            Use document value “{date ? fmtDate(trimmed) : trimmed}”
          </label>
        </div>
      )}
      {!conflict && trimmed !== '' && current === '' && (
        <p className="text-xs text-muted mt-1">Will be set on the client.</p>
      )}
      {trimmed !== '' && current === trimmed && (
        <p className="text-xs text-muted mt-1">Matches the client record — nothing to write.</p>
      )}
    </div>
  );
}

function CertificateStep({ client, type, askFyEnd, onSkip, onAttached }: {
  client: ClientOutput;
  type: ClientDocumentType;
  askFyEnd: boolean;
  onSkip: () => void;
  onAttached: (client: ClientOutput) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [doc, setDoc] = useState<OnboardingDocumentOutput | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ConfirmClientDocumentOutput | null>(null);

  const [values, setValues] = useState<Record<'legalName' | 'trn', string>>({ legalName: '', trn: '' });
  const [choices, setChoices] = useState<Record<'legalName' | 'trn', 'keep' | 'use'>>({ legalName: 'keep', trn: 'keep' });
  const [fyEnd, setFyEnd] = useState(client.fyEnd?.slice(0, 10) ?? '');
  const [fyChoice, setFyChoice] = useState<'keep' | 'use'>('keep');
  const [taxDates, setTaxDates] = useState(() => taxReviewValues(null));

  const label = CLIENT_DOCUMENT_TYPE_LABELS[type];

  // Auth-gated preview — fetch as blob (same pattern as the vault review modal).
  const preview = useQuery({
    queryKey: ['onboarding-document-file', doc?.id],
    enabled: doc !== null,
    queryFn: async () => {
      const res = await fetch(`/v1/onboarding/documents/${doc!.id}/file`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      return URL.createObjectURL(await res.blob());
    },
    staleTime: Infinity,
  });
  useEffect(() => () => { if (preview.data) URL.revokeObjectURL(preview.data); }, [preview.data]);

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    if (!file) { setError(`Choose a ${label.toLowerCase()} file first.`); return; }
    if (file.size > MAX_FILE_BYTES) { setError('File too large — max 20 MB.'); return; }
    setError(null);
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('type', type);
      fd.append('file', file);
      const res = await fetch('/v1/onboarding/documents', { method: 'POST', headers: authHeaders(), body: fd });
      const body = await res.json() as { ok?: boolean; data?: OnboardingUploadOutput; error?: { message?: string } };
      if (!res.ok || !body.ok || !body.data) {
        setError(body.error?.message ?? `Error ${res.status}: ${res.statusText}`);
        return;
      }
      const staged = body.data.document;
      setDoc(staged);
      setTaxDates(taxReviewValues(staged.extracted));
      if (askFyEnd) setFyEnd(staged.extracted?.firstPeriodEndDate ?? client.fyEnd?.slice(0, 10) ?? '');
      setValues({
        legalName: staged.extracted?.legalName ?? '',
        trn: staged.extracted?.trn ?? '',
      });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAttach(event: React.MouseEvent<HTMLButtonElement>) {
    if (!doc || !validateDateInputs(event.currentTarget.closest('[data-date-form]') ?? document)) return;
    setError(null);
    setSubmitting(true);
    try {
      const fields: { legalName?: string | null; trn?: string | null; fyEnd?: string | null } = {};
      const applyConflicts: ConfirmableField[] = [];
      (['legalName', 'trn'] as const).forEach((f) => {
        const current = (client[f] ?? '').trim();
        const next = values[f].trim();
        if (next === '' || next === current) return;
        if (current === '') {
          fields[f] = next;
        } else if (choices[f] === 'use') {
          fields[f] = next;
          applyConflicts.push(f);
        }
        // choice 'keep' → omit the field entirely
      });
      if (askFyEnd) {
        const current = client.fyEnd ? client.fyEnd.slice(0, 10) : '';
        if (fyEnd && fyEnd !== current && (!current || fyChoice === 'use')) {
          fields.fyEnd = fyEnd;
          if (current !== '') applyConflicts.push('fyEnd');
        }
      }
      const res = await fetch('/v1/onboarding/attach', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ stagingDocumentId: doc.id, clientId: client.id, fields, applyConflicts, taxFields: taxReviewPayload(taxDates) }),
      });
      const body = await res.json() as { ok?: boolean; data?: ConfirmClientDocumentOutput; error?: { message?: string } };
      if (!res.ok || !body.ok || !body.data) {
        setError(body.error?.message ?? `Error ${res.status}: ${res.statusText}`);
        return;
      }
      setResult(body.data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleContinue() {
    // Refetch the client so later tiers see the values this tier wrote.
    const res = await fetch(`/v1/clients/${client.id}`, { headers: authHeaders() });
    if (res.ok) {
      const body = await res.json() as { ok: true; data: ClientOutput };
      onAttached(body.data);
    } else {
      onAttached(client);
    }
  }

  if (!doc) {
    return (
      <form onSubmit={handleUpload} className="max-w-lg mx-auto space-y-3 py-4">
        <p className="text-sm text-white">
          Add the <span className="font-medium">{label.toLowerCase()}</span> for {client.name}? It attaches straight
          into the client's document vault as confirmed.
        </p>
        <input
          type="file"
          accept="application/pdf,image/jpeg,image/jpg,image/png,image/webp"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="w-full text-sm text-muted file:mr-3 file:bg-bg-base file:border file:border-border file:rounded-lg file:px-3 file:py-1.5 file:text-sm file:text-white hover:file:border-accent"
        />
        <p className="text-xs text-muted">PDF, JPG, PNG or WebP — max 20 MB.</p>
        {error && <p className="text-xs text-rose-400 bg-rose-950/30 border border-rose-800 rounded-lg px-3 py-2">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onSkip} className="text-sm text-muted hover:text-white px-3 py-1.5">
            Skip
          </button>
          <button
            type="submit"
            disabled={submitting || !file}
            className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
          >
            {submitting ? 'Uploading & extracting…' : 'Upload & extract'}
          </button>
        </div>
      </form>
    );
  }

  const isImage = doc.mime.startsWith('image/');

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      <div className="lg:w-1/2 min-h-[300px] bg-bg-base border border-border rounded-lg overflow-hidden flex items-center justify-center">
        {preview.isLoading && <span className="text-muted text-sm p-6">Loading preview…</span>}
        {preview.error && <span className="text-rose-400 text-sm p-6">{(preview.error as Error).message}</span>}
        {preview.data && isImage && (
          <img src={preview.data} alt={doc.originalName} className="max-h-[60vh] w-full object-contain" />
        )}
        {preview.data && !isImage && (
          <iframe src={preview.data} title={doc.originalName} className="w-full h-[60vh]" />
        )}
      </div>

      <div data-date-form className="lg:w-1/2 space-y-3">
        {doc.extractionError && (
          <p className="text-xs text-rose-300 bg-rose-950/30 border border-rose-800 rounded-lg px-3 py-2">
            AI extraction failed{doc.extractionError ? `: ${doc.extractionError}` : ''}. Fill the fields manually against the document.
          </p>
        )}
        <p className="text-xs text-muted">
          {label} · the trade license values win by default — choose the document value only where it is correct.
        </p>

        <AttachFieldRow
          label={ATTACH_FIELD_LABELS.legalName}
          current={client.legalName ?? ''}
          value={values.legalName}
          onChange={(v) => setValues((s) => ({ ...s, legalName: v }))}
          choice={choices.legalName}
          onChoice={(c) => setChoices((s) => ({ ...s, legalName: c }))}
        />
        <AttachFieldRow
          label={ATTACH_FIELD_LABELS.trn}
          current={client.trn ?? ''}
          value={values.trn}
          onChange={(v) => setValues((s) => ({ ...s, trn: v }))}
          choice={choices.trn}
          onChoice={(c) => setChoices((s) => ({ ...s, trn: c }))}
        />
        <TaxCertificateDates kind={askFyEnd ? 'CT' : 'VAT'} values={taxDates} extracted={doc.extracted} onChange={(next) => {
          if (askFyEnd && (!fyEnd || fyEnd === taxDates.firstPeriodEndDate)) setFyEnd(next.firstPeriodEndDate);
          setTaxDates(next);
        }} />
        {askFyEnd && <>
          <AttachFieldRow date label={ATTACH_FIELD_LABELS.fyEnd} current={client.fyEnd?.slice(0, 10) ?? ''} value={fyEnd} onChange={setFyEnd} choice={fyChoice} onChoice={setFyChoice} />
          <p className="text-xs text-muted">Suggested from the reviewed first period end. Its day/month describes the recurring financial year; the original first period stays on this certificate. Missing evidence leaves it blank; no December year end is assumed.</p>
        </>}

        {error && <p className="text-xs text-rose-400 bg-rose-950/30 border border-rose-800 rounded-lg px-3 py-2">{error}</p>}

        {result ? (
          <div className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 space-y-1">
            <p>{label} attached to the vault as confirmed.</p>
            {result.written.length > 0 && <p>Written to client: {result.written.join(', ')}.</p>}
            {result.skippedConflicts.length > 0 && <p>Kept existing values for: {result.skippedConflicts.join(', ')}.</p>}
          </div>
        ) : (
          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={handleAttach}
              disabled={submitting}
              className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
            >
              {submitting ? 'Attaching…' : 'Attach & apply'}
            </button>
          </div>
        )}
        {result && (
          <div className="flex justify-end pt-1">
            <button
              onClick={handleContinue}
              className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
            >
              Continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function WizardField({ label, required, hint, children }: { label: string; required?: boolean; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-muted mb-1">
        {label} {required && <span className="text-rose-400">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-muted mt-1">{hint}</p>}
    </div>
  );
}
